import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import { ArrowRight, ArrowDown, Layers, CupSoda, Zap } from "lucide-react";
import { CATEGORIES, ACCENTS } from "@/data/catalog";
import BulkInquiry from "@/components/BulkInquiry";
import InstagramFeed from "@/components/InstagramFeed";

const IMGS = {
  heroMain:
    "https://images.unsplash.com/photo-1648466104157-53bb5b191c72?crop=entropy&cs=srgb&fm=jpg&q=85",
  heroFloat:
    "https://images.unsplash.com/photo-1542291026-7eec264c27ff?auto=format&fit=crop&w=1200&q=80",
  stickers:
    "https://images.unsplash.com/photo-1705039228815-a54991a30167?crop=entropy&cs=srgb&fm=jpg&q=85",
  mug: "https://images.unsplash.com/photo-1514228742587-6b1558fcca3d?auto=format&fit=crop&w=1200&q=80",
  tees: "https://images.unsplash.com/photo-1521572163474-6864f9cf17ab?auto=format&fit=crop&w=1200&q=80",
  sneakers:
    "https://images.pexels.com/photos/374080/pexels-photo-374080.jpeg?auto=compress&cs=tinysrgb&w=940",
  backpack:
    "https://images.unsplash.com/photo-1553062407-98eeb64c6a62?auto=format&fit=crop&w=1200&q=80",
  hoodie:
    "https://images.unsplash.com/photo-1594587002961-1a75dfaf34b8?crop=entropy&cs=srgb&fm=jpg&q=85",
};

const PORTFOLIO_WORKS = [
  { title: "Branded Hoodies", img: IMGS.heroMain },
  { title: "Custom Vinyl Stickers", img: IMGS.stickers },
  {
    title: "Embroidered Polos",
    img: "https://images.unsplash.com/photo-1523381210434-271e8be1f52b?auto=format&fit=crop&w=800&q=60",
  },
  { title: "Glossy Mugs", img: IMGS.mug },
];

const RevealLine = ({ children, delay, className }) => (
  <span className="block overflow-hidden">
    <motion.span
      initial={{ y: "110%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.8, delay, ease: [0.16, 1, 0.3, 1] }}
      className={`block ${className}`}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yMain = useTransform(scrollYProgress, [0, 1], [0, 120]);
  const yFloat = useTransform(scrollYProgress, [0, 1], [0, -80]);
  const rotate = useTransform(scrollYProgress, [0, 1], [-4, 4]);

  return (
    <section ref={ref} className="halftone relative overflow-hidden pb-20 pt-40 lg:pt-48">
      <div className="pointer-events-none absolute -left-40 top-20 h-[500px] w-[500px] rounded-full bg-brand-cyan/10 blur-[120px]" />
      <div className="pointer-events-none absolute -right-40 bottom-0 h-[500px] w-[500px] rounded-full bg-brand-magenta/10 blur-[120px]" />

      <div className="relative mx-auto grid max-w-7xl items-center gap-14 px-4 sm:px-6 lg:grid-cols-[1.15fr_0.85fr]">
        <div>
          <motion.p
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ delay: 0.15 }}
            className="font-mono text-xs uppercase tracking-[0.4em] text-zinc-400"
          >
            TLF DESIGNZ — Custom Print Studio
          </motion.p>
          <h1
            data-testid="hero-headline"
            className="mt-5 font-display text-6xl font-black uppercase leading-[0.9] tracking-tight sm:text-8xl lg:text-9xl"
          >
            <RevealLine delay={0.25} className="text-brand-cyan logo-shadow">
              Print
            </RevealLine>
            <RevealLine delay={0.4} className="text-brand-magenta logo-shadow">
              Brand
            </RevealLine>
            <RevealLine delay={0.55} className="text-brand-yellow logo-shadow">
              Create
            </RevealLine>
          </h1>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.7 }}
            data-testid="hero-subheadline-banner"
            className="mt-6 inline-block bg-brand-yellow px-4 py-2.5 font-display text-sm font-black uppercase tracking-widest text-ink sm:text-base"
          >
            Perfect for local apparel brands, corporate uniforms, and fast event printing.
          </motion.p>
          <motion.p
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.85 }}
            className="mt-6 max-w-lg text-base leading-relaxed text-zinc-200 sm:text-lg"
          >
            Apparel, drinkware, stickers and one-of-one sneakers — printed at production
            grade with CMYK-calibrated color and 24-hour express turnaround.
          </motion.p>
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 1 }}
            className="mt-9 flex flex-wrap gap-4"
          >
            <Link
              to="/configure/stickers-labels"
              data-testid="hero-cta-configure"
              className="group flex items-center gap-2 bg-brand-yellow px-7 py-4 font-display text-base font-black uppercase tracking-widest text-ink transition-colors hover:bg-brand-cyan"
            >
              Configure Custom Order
              <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
            </Link>
            <a
              href="#categories"
              data-testid="hero-cta-categories"
              className="flex items-center gap-2 border border-white/25 px-7 py-4 font-display text-base font-black uppercase tracking-widest text-white transition-colors hover:border-brand-magenta hover:text-brand-magenta"
            >
              View Categories <ArrowDown className="h-4 w-4" />
            </a>
          </motion.div>
        </div>

        <div className="relative hidden h-[520px] lg:block">
          <motion.div style={{ y: yMain, rotate }} className="absolute right-8 top-4 w-72">
            <div className="border border-white/15 bg-ink-2 p-3 shadow-2xl">
              <img src={IMGS.heroMain} alt="Custom streetwear hoodie" className="aspect-[3/4] w-full object-cover" />
              <div className="flex justify-between py-2 font-mono text-[9px] uppercase tracking-widest text-zinc-500">
                <span>TLF-001 / Hoodie</span>
                <span className="text-brand-cyan">CMYK</span>
              </div>
            </div>
            <span className="absolute -left-2 -top-2 h-4 w-4 border-l-2 border-t-2 border-brand-cyan" />
            <span className="absolute -bottom-2 -right-2 h-4 w-4 border-b-2 border-r-2 border-brand-magenta" />
          </motion.div>
          <motion.div style={{ y: yFloat }} className="absolute bottom-0 left-0 w-56">
            <motion.div
              initial={{ opacity: 0, y: 40 }}
              animate={{ opacity: 1, y: 0 }}
              transition={{ delay: 0.7, duration: 0.7 }}
              className="border border-white/15 bg-ink-2 p-3 shadow-2xl"
            >
              <img src={IMGS.heroFloat} alt="Custom sneaker" className="aspect-square w-full object-cover" />
              <div className="flex justify-between py-2 font-mono text-[9px] uppercase tracking-widest text-zinc-500">
                <span>1-of-1 Custom</span>
                <span className="text-brand-yellow">300 DPI</span>
              </div>
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const EditorialMarquee = () => {
  const words = [
    { w: "Apparel", c: "text-brand-yellow" },
    { w: "Drinkware", c: "text-brand-cyan" },
    { w: "Stickers", c: "text-brand-magenta" },
    { w: "Sneakers", c: "text-brand-yellow" },
    { w: "Tumblers", c: "text-brand-cyan" },
    { w: "Jerseys", c: "text-brand-magenta" },
    { w: "Merch", c: "text-white" },
  ];
  const row = (
    <>
      {words.map(({ w, c }, i) => (
        <span key={i} className="flex items-center">
          <span className={`font-display text-5xl font-black uppercase tracking-tight sm:text-7xl ${c}`}>
            {w}
          </span>
          <span className={`mx-6 h-3 w-3 rotate-45 ${["bg-brand-cyan", "bg-brand-magenta", "bg-brand-yellow"][i % 3]}`} />
        </span>
      ))}
    </>
  );
  return (
    <div className="overflow-hidden border-y border-white/10 py-6">
      <div className="flex w-max animate-marquee items-center whitespace-nowrap">
        <div className="flex items-center">{row}</div>
        <div className="flex items-center">{row}</div>
      </div>
    </div>
  );
};

const CHAPTERS = [
  { n: "01", title: "Precision", stat: "300 DPI", desc: "Every file preflighted, every print calibrated. Registration-tight output on every run.", accent: "text-brand-cyan" },
  { n: "02", title: "Speed", stat: "24 HR", desc: "Express turnaround on most custom work. Rush jobs are our comfort zone.", accent: "text-brand-magenta" },
  { n: "03", title: "Volume", stat: "100K+", desc: "Prints delivered for brands, events and crews — from one-offs to full production runs.", accent: "text-brand-yellow" },
];

const Manifesto = () => (
  <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6">
    <div className="grid gap-px border border-white/10 bg-white/10 md:grid-cols-3">
      {CHAPTERS.map((c, i) => (
        <motion.div
          key={c.n}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: i * 0.12, duration: 0.5 }}
          className="halftone bg-ink-2 p-8"
          data-testid={`manifesto-chapter-${c.n}`}
        >
          <p className={`font-mono text-xs tracking-[0.4em] ${c.accent}`}>{c.n}</p>
          <p className="mt-4 font-display text-5xl font-black uppercase tracking-tight text-white">
            {c.title}
          </p>
          <p className={`mt-1 font-display text-2xl font-bold ${c.accent}`}>{c.stat}</p>
          <p className="mt-3 text-sm leading-relaxed text-zinc-200">{c.desc}</p>
        </motion.div>
      ))}
    </div>
  </section>
);

const CategoryGrid = () => (
  <section id="categories" className="mx-auto max-w-7xl scroll-mt-28 px-4 pb-24 sm:px-6">
    <div className="mb-10 flex items-end justify-between">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.4em] text-brand-cyan">The Catalog</p>
        <h2 className="mt-2 font-display text-4xl font-black uppercase tracking-tight text-white sm:text-5xl">
          Seven Ways to Make It Yours
        </h2>
      </div>
      <div className="hidden h-8 w-40 grid-cols-4 md:grid">
        <div className="bg-brand-cyan" />
        <div className="bg-brand-magenta" />
        <div className="bg-brand-yellow" />
        <div className="bg-white" />
      </div>
    </div>
    <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
      {CATEGORIES.map((c, i) => {
        const accent = ACCENTS[c.accent];
        const Icon = c.icon;
        return (
          <motion.div
            key={c.slug}
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true, margin: "-60px" }}
            transition={{ delay: (i % 4) * 0.08, duration: 0.5 }}
            className={i === 0 ? "sm:col-span-2 lg:row-span-2" : ""}
          >
            <Link
              to={`/shop/${c.slug}`}
              data-testid={`category-grid-item-${c.slug}`}
              className={`group relative flex h-full flex-col justify-between overflow-hidden border border-white/10 bg-ink-2 p-6 transition-colors duration-300 hover:${accent.border} ${i === 0 ? "min-h-[320px]" : "min-h-[220px]"}`}
            >
              <div className="halftone pointer-events-none absolute inset-0 opacity-60" />
              <div className="relative flex items-start justify-between">
                <span className="font-mono text-xs tracking-widest text-zinc-600">
                  {String(i + 1).padStart(2, "0")}
                </span>
                <Icon
                  className={`${i === 0 ? "h-14 w-14" : "h-9 w-9"} ${accent.text} transition-transform duration-500 group-hover:-rotate-12 group-hover:scale-110`}
                  strokeWidth={1.25}
                />
              </div>
              <div className="relative">
                <h3 className={`font-display font-black uppercase leading-none text-white ${i === 0 ? "text-4xl" : "text-2xl"}`}>
                  {c.name}
                </h3>
                <p className="mt-2 max-w-xs text-xs leading-relaxed text-zinc-200">{c.blurb}</p>
                <div className="mt-3 flex flex-wrap gap-x-2 gap-y-1">
                  {c.subs.slice(0, i === 0 ? 6 : 3).map((s) => (
                    <span key={s.slug} className="font-mono text-[9px] uppercase tracking-wider text-zinc-500">
                      {s.name}
                    </span>
                  ))}
                </div>
                <span className={`mt-4 inline-flex items-center gap-1.5 font-display text-xs font-bold uppercase tracking-widest ${accent.text}`}>
                  Shop now <ArrowRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          </motion.div>
        );
      })}
    </div>
  </section>
);

const BuilderTeaser = () => (
  <section className="border-y border-white/10 bg-ink-2 py-24">
    <div className="mx-auto max-w-7xl px-4 sm:px-6">
      <p className="font-mono text-xs uppercase tracking-[0.4em] text-brand-magenta">Live Calculators</p>
      <h2 className="mt-2 font-display text-4xl font-black uppercase tracking-tight text-white sm:text-5xl">
        Build It. Price It. Print It.
      </h2>
      <div className="mt-10 grid gap-6 md:grid-cols-2">
        {[
          {
            title: "Stickers & Labels",
            desc: '2", 3" and 4" custom sheets in economy, standard or laminated finishes. Instant sheet pricing.',
            img: IMGS.stickers,
            to: "/configure/stickers-labels",
            accent: "text-brand-magenta",
            border: "hover:border-brand-magenta",
            icon: Layers,
            testid: "teaser-stickers",
          },
          {
            title: "Snow Globe Tumblers",
            desc: "Glitter-filled double-wall tumblers in 16oz and 24oz with sticker add-ons. Upload art, watch the price react.",
            img: IMGS.mug,
            to: "/configure/snow-globe-tumblers",
            accent: "text-brand-cyan",
            border: "hover:border-brand-cyan",
            icon: CupSoda,
            testid: "teaser-tumblers",
          },
        ].map((b, i) => (
          <motion.div
            key={b.title}
            initial={{ opacity: 0, y: 28 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            transition={{ delay: i * 0.12, duration: 0.5 }}
          >
            <Link
              to={b.to}
              data-testid={b.testid}
              className={`group grid overflow-hidden border border-white/10 bg-ink transition-colors sm:grid-cols-2 ${b.border}`}
            >
              <div className="relative overflow-hidden">
                <img
                  src={b.img}
                  alt={b.title}
                  className="h-48 w-full object-cover transition-transform duration-700 group-hover:scale-105 sm:h-full"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-ink/70 to-transparent" />
              </div>
              <div className="flex flex-col justify-between p-6">
                <div>
                  <b.icon className={`h-8 w-8 ${b.accent}`} strokeWidth={1.25} />
                  <h3 className="mt-4 font-display text-2xl font-black uppercase text-white">{b.title}</h3>
                  <p className="mt-2 text-sm leading-relaxed text-zinc-200">{b.desc}</p>
                </div>
                <span className={`mt-6 inline-flex items-center gap-2 font-display text-sm font-bold uppercase tracking-widest ${b.accent}`}>
                  Launch Builder <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
                </span>
              </div>
            </Link>
          </motion.div>
        ))}
      </div>
    </div>
  </section>
);

const Showcase = () => (
  <section className="mx-auto max-w-7xl px-4 py-24 sm:px-6">
    <p className="font-mono text-xs uppercase tracking-[0.4em] text-brand-yellow">Fresh Off The Press</p>
    <h2 className="mt-2 font-display text-4xl font-black uppercase tracking-tight text-white sm:text-5xl">
      Studio Output
    </h2>
    <div className="mt-10 grid grid-cols-2 gap-4 lg:grid-cols-4">
      {[
        { img: IMGS.tees, label: "DTF Tee Run", tag: "Apparel" },
        { img: IMGS.heroFloat, label: "Custom 1-of-1", tag: "Sneakers" },
        { img: IMGS.backpack, label: "Crew Packs", tag: "Bags" },
        { img: IMGS.hoodie, label: "Blackout Hoodie", tag: "Apparel" },
      ].map((s, i) => (
        <motion.figure
          key={s.label}
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: i * 0.08, duration: 0.5 }}
          className="group relative overflow-hidden border border-white/10"
          data-testid={`showcase-item-${i}`}
        >
          <img
            src={s.img}
            alt={s.label}
            className="aspect-[3/4] w-full object-cover transition-transform duration-700 group-hover:scale-105"
          />
          <div className="absolute inset-0 bg-gradient-to-t from-ink/90 via-transparent to-transparent" />
          <figcaption className="absolute bottom-0 left-0 right-0 p-4">
            <p className="font-mono text-[9px] uppercase tracking-widest text-brand-cyan">{s.tag}</p>
            <p className="font-display text-lg font-bold uppercase text-white">{s.label}</p>
          </figcaption>
        </motion.figure>
      ))}
    </div>
  </section>
);

const BulkSection = () => (
  <section className="border-t border-white/10 bg-ink-2 py-24">
    <div className="mx-auto grid max-w-7xl gap-12 px-4 sm:px-6 lg:grid-cols-[0.9fr_1.1fr]">
      <div>
        <p className="font-mono text-xs uppercase tracking-[0.4em] text-brand-yellow">Business & Events</p>
        <h2 className="mt-2 font-display text-4xl font-black uppercase leading-none tracking-tight text-white sm:text-5xl">
          Quick Order /<br />Bulk Inquiry
        </h2>
        <p className="mt-5 max-w-md text-sm leading-relaxed text-zinc-200">
          Outfit a whole crew, stock your merch table, or paper the city with flyers.
          Send the spec — we return a production quote within one business day.
        </p>
        <ul className="mt-8 space-y-3">
          {["Volume price breaks at 25 / 50 / 100+", "Free digital proof before we print", "Rush turnaround for event deadlines"].map((t) => (
            <li key={t} className="flex items-center gap-3 text-sm text-zinc-300">
              <Zap className="h-4 w-4 shrink-0 text-brand-yellow" /> {t}
            </li>
          ))}
        </ul>
      </div>
      <div className="border border-white/10 bg-ink p-6 sm:p-8">
        <BulkInquiry />
      </div>
    </div>
  </section>
);

const Home = () => (
  <div data-testid="home-page">
    <Hero />
    <EditorialMarquee />
    <InstagramFeed
      works={PORTFOLIO_WORKS}
      kicker="Recent Studio Work"
      heading="The Portfolio"
      testid="portfolio"
    />
    <Manifesto />
    <CategoryGrid />
    <BuilderTeaser />
    <Showcase />
    <BulkSection />
    <InstagramFeed />
  </div>
);

export default Home;
